import {
  ArrowLeftOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { IconSparkles } from '@tabler/icons-react'
import { Button, Drawer, Spin, Tooltip, message, theme } from 'antd'
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import type { GraviMessageOut } from '@/api/gravitutor/types'
import { useCancelTask } from '@/api/gravitutor/useCancelTask'
import { useGraviStream } from '@/api/gravitutor/useGraviStream'
import { useSubmitTurn } from '@/api/gravitutor/useSubmitTurn'
import {
  type GraviTaskDoc,
  useGraviTaskStatus,
} from '@/api/gravitutor/useTaskStatus'
import {
  useCreateThread,
  useDeleteThread,
  useThreadDetail,
  useThreadsList,
} from '@/api/gravitutor/useThreads'

import {
  type ComposerAttachment,
  attachmentErrorMessage,
  filesToAttachments,
} from './attachmentUtils'
import { Composer } from './components/Composer'
import { ConversationsPanel } from './components/ConversationsPanel'
import { MessageList, type TurnState } from './components/MessageList'
import { buildSubmitTurnBody, usePageContext } from './pageContext'
import { useGraviChatStore } from './store'

// How long a turn can sit running with zero progress before we treat it as stalled.
const STALL_MS = 90_000

// Default (non-expanded) drawer width.
const DEFAULT_DRAWER_WIDTH = 640

// When the drawer is expanded full-screen, the chat content is constrained to a
// centered column of this width (matching the reading-width convention of chat apps)
// rather than stretching edge-to-edge across the viewport.
const EXPANDED_COLUMN_MAX_WIDTH = 883

export type GraviChatDrawerProps = {
  open: boolean
  onClose: () => void
}

// The drawer renders the proxy's `GraviMessageOut` shape directly; normalize the
// loosely-typed tool_calls / output_files / attachments into the message-list shape.
function toLocalMessageOut(m: GraviMessageOut) {
  return {
    id: m.id,
    role: m.role,
    text: m.text,
    task_id: m.task_id,
    finalized: m.finalized,
    created_at: m.created_at,
    tool_calls: m.tool_calls.map((tc) => ({
      name: typeof tc.name === 'string' ? tc.name : undefined,
      input:
        tc.input != null && typeof tc.input === 'object'
          ? (tc.input as Record<string, unknown>)
          : {},
    })),
    output_files: (m.output_files ?? []).map((f) => ({
      filename: f.filename,
      mime_type: f.mime_type,
      size_bytes: f.size_bytes,
    })),
    attachments: (m.attachments ?? []).map((a) => ({
      filename: a.filename,
      mime_type: a.mime_type,
      size_bytes: a.size_bytes,
      content_b64: a.content_b64 ?? null,
      url: a.url ?? null,
    })),
  }
}

type LocalMessage = ReturnType<typeof toLocalMessageOut>

export function GraviChatDrawer({ open, onClose }: GraviChatDrawerProps) {
  const { token } = theme.useToken()
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [view, setView] = useState<'chat' | 'conversations'>('chat')
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [pendingMessage, setPendingMessage] = useState<{
    threadId: string
    message: LocalMessage
  } | null>(null)
  const bodyDragRef = useRef<HTMLDivElement>(null)
  // Keep the newest attachments readable inside the native drop listener
  // (which closes over a single render) so dedup uses current state.
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments

  const threads = useThreadsList()
  const detail = useThreadDetail(activeThreadId ?? undefined)
  const createThread = useCreateThread()
  const deleteThread = useDeleteThread()
  const submitTurn = useSubmitTurn()
  const cancelTask = useCancelTask()
  const pageContext = usePageContext()
  const status = useGraviChatStore((s) => s.status)
  const active = useGraviChatStore((s) => s.active)
  const errorText = useGraviChatStore((s) => s.errorText)
  const submitError = useGraviChatStore((s) => s.submitError)

  // When the live stream closes, refetch the thread once so any messages tutor has
  // already finalized show immediately. If the task is still running, the
  // useGraviTaskStatus poll below refetches again on completion (tutor is the
  // source of truth for the canonical reply — there is no webhook to wait on).
  const refetchDetail = detail.refetch
  const onSettled = useCallback(() => {
    useGraviChatStore.getState().resetStreamState()
    refetchDetail()
  }, [refetchDetail])

  const { start, stop } = useGraviStream({ onSettled })

  // Auto-select first thread when list loads.
  useEffect(() => {
    if (!activeThreadId && threads.data && threads.data.length > 0) {
      setActiveThreadId(threads.data[0]!.id)
    }
  }, [activeThreadId, threads.data])

  // On thread switch, re-attach if there's an active task persisted for it.
  useEffect(() => {
    if (!activeThreadId) return
    const raw = sessionStorage.getItem(`gravi.active.${activeThreadId}`)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as { taskId: string }
      start({ threadId: activeThreadId, taskId: parsed.taskId })
    } catch {
      // ignore malformed
    }
  }, [activeThreadId, start])

  // Pending attachments belong to the composer for the current thread only.
  useEffect(() => {
    setAttachments([])
  }, [activeThreadId])

  async function handleNew() {
    const created = await createThread.mutateAsync({})
    setActiveThreadId(created.id)
    setView('chat')
    threads.refetch()
  }

  function handleSelect(id: string) {
    setActiveThreadId(id)
    setView('chat')
  }

  async function handleDelete(id: string) {
    await deleteThread.mutateAsync(id)
    if (activeThreadId === id) setActiveThreadId(null)
    threads.refetch()
  }

  async function handleSend(text: string) {
    // Clear any leftover error from a previous attempt so the UI starts clean.
    // Stream state (status/active) is also reset so a stuck 'reconnecting' from
    // a prior failed stream doesn't leak into the new send's turnState.
    useGraviChatStore.getState().setSubmitError(null)
    useGraviChatStore.getState().resetStreamState()

    let threadId = activeThreadId
    if (!threadId) {
      const created = await createThread.mutateAsync({})
      threadId = created.id
      setActiveThreadId(threadId)
      threads.refetch()
    }

    const optimistic: LocalMessage = {
      id: `optimistic-${crypto.randomUUID()}`,
      role: 'user',
      text,
      task_id: null,
      finalized: true,
      created_at: new Date().toISOString(),
      tool_calls: [],
      output_files: [],
      attachments: attachments.map((a) => ({
        filename: a.filename,
        mime_type: a.mime_type,
        size_bytes: a.size_bytes,
        content_b64: a.content_b64,
        url: null,
      })),
    }
    setPendingMessage({ threadId, message: optimistic })

    try {
      const result = await submitTurn.mutateAsync({
        threadId,
        body: buildSubmitTurnBody({ text, attachments }, pageContext),
      })
      setPendingMessage((p) =>
        p ? { ...p, message: { ...p.message, task_id: result.task_id } } : p,
      )
      setAttachments([])
      detail.refetch()
      start({ threadId, taskId: result.task_id })
    } catch {
      setPendingMessage(null)
    }
  }

  async function handleStop() {
    if (!active) return
    useGraviChatStore.getState().setStatus('cancelling')
    try {
      await cancelTask.mutateAsync(active.taskId)
      stop()
      useGraviChatStore.getState().resetStreamState()
    } catch {
      // useCancelTask.onError already wrote submitError and restored status.
    }
  }

  // Drawer-wide drag-and-drop. Listeners live on the whole drawer panel
  // (.ant-drawer-content = header + body + footer) so a single enter/leave
  // boundary drives the overlay — attaching to the body and footer separately
  // double-counts across their shared seam and can leave the overlay stuck.
  useEffect(() => {
    const panel = bodyDragRef.current?.closest(
      '.ant-drawer-content',
    ) as HTMLElement | null
    if (!panel) return
    let depth = 0
    const isFile = (e: DragEvent) =>
      !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')
    const onEnter = (e: DragEvent) => {
      if (!isFile(e)) return
      e.preventDefault()
      depth += 1
      setIsDragging(true)
    }
    const onOver = (e: DragEvent) => {
      if (isFile(e)) e.preventDefault()
    }
    const onLeave = () => {
      depth = Math.max(0, depth - 1)
      if (depth === 0) setIsDragging(false)
    }
    const onDrop = async (e: DragEvent) => {
      if (!isFile(e)) return
      e.preventDefault()
      depth = 0
      setIsDragging(false)
      const files = Array.from(e.dataTransfer?.files ?? [])
      if (files.length === 0) return
      const res = await filesToAttachments(files, attachmentsRef.current)
      if (res.error) {
        message.error(attachmentErrorMessage(res.error))
        return
      }
      setAttachments((prev) => [...prev, ...res.attachments])
    }
    panel.addEventListener('dragenter', onEnter)
    panel.addEventListener('dragover', onOver)
    panel.addEventListener('dragleave', onLeave)
    panel.addEventListener('drop', onDrop)
    return () => {
      panel.removeEventListener('dragenter', onEnter)
      panel.removeEventListener('dragover', onOver)
      panel.removeEventListener('dragleave', onLeave)
      panel.removeEventListener('drop', onDrop)
    }
  }, [open])

  const serverMessages = (detail.data?.messages ?? []).map(toLocalMessageOut)
  const showOptimistic =
    pendingMessage !== null &&
    pendingMessage.threadId === activeThreadId &&
    !(
      pendingMessage.message.task_id != null &&
      serverMessages.some(
        (m) =>
          m.role === 'user' && m.task_id === pendingMessage.message.task_id,
      )
    )
  const messages = showOptimistic
    ? [...serverMessages, pendingMessage.message]
    : serverMessages

  // The store holds a single in-flight stream. Only honor its live state when the
  // stream's thread is the one currently on screen — otherwise switching chats
  // mid-turn drags the spinner/timer/streamed text/Stop button onto the wrong
  // conversation (the stream keeps running in the background and re-attaches when
  // its thread is reopened).
  const streamForActiveThread =
    active != null && active.threadId === activeThreadId

  // A reply is "pending" when the last message is the user's and no assistant reply
  // has landed. We carry the task_id on the user message so this survives a refresh.
  const lastMessage = messages[messages.length - 1]
  const liveStreaming =
    streamForActiveThread &&
    (status === 'streaming' || status === 'reconnecting')
  const pendingTaskId =
    lastMessage?.role === 'user'
      ? (lastMessage.task_id ?? undefined)
      : undefined

  // Poll gravitutor's task status whenever a reply is pending — even while the live
  // stream is open. gravitutor holds the SSE connection open for the whole "running"
  // window, so a hung worker (no deltas) would otherwise look like a healthy stream
  // forever. The poll's progress fields let us tell genuine work from a stall.
  const taskStatus = useGraviTaskStatus(pendingTaskId, Boolean(pendingTaskId))
  const taskDoc = taskStatus.data as GraviTaskDoc | undefined

  // When the polled task completes, pull the canonical reply into the thread.
  const refetchOnComplete = detail.refetch
  useEffect(() => {
    if (taskDoc?.status === 'completed') refetchOnComplete()
  }, [taskDoc?.status, refetchOnComplete])

  const pendingForMs = lastMessage
    ? Date.now() - new Date(lastMessage.created_at).getTime()
    : 0

  let turnState: TurnState = null
  let turnErrorText: string | undefined
  if (submitError) {
    // Non-stream failure (couldn't submit the turn, etc) — takes precedence so the
    // user sees the failure immediately. Cleared on next handleSend.
    turnState = 'error'
    turnErrorText = submitError
  } else if (streamForActiveThread && status === 'error') {
    turnState = 'error'
    turnErrorText = errorText ?? undefined
  } else if (pendingTaskId) {
    const s = taskDoc?.status
    if (s === 'failed') {
      turnState = 'error'
      turnErrorText =
        taskDoc?.result?.error?.message ?? 'Gravi failed to complete this task.'
    } else if (s === 'cancelled') {
      turnState = 'error'
      turnErrorText = 'This task was cancelled.'
    } else if (s === 'running' || s === 'queued') {
      // Stalled = gravitutor reports the task running but it has made zero progress
      // (no tokens, no deltas) for longer than the threshold — i.e. a hung worker,
      // regardless of whether our SSE connection is still nominally open.
      const noProgress =
        (taskDoc?.progress?.tokens_in ?? 0) === 0 &&
        (taskDoc?.progress?.tokens_out ?? 0) === 0 &&
        !taskDoc?.progress?.last_delta_at
      turnState = noProgress && pendingForMs > STALL_MS ? 'stalled' : 'working'
    } else if (taskStatus.isError) {
      // Couldn't load the task (e.g. 404 / expired) — surface rather than spin.
      turnState = 'error'
      turnErrorText = "Gravi couldn't load this task's status."
    } else {
      // 'completed' (reply refetch pending) or status not yet known — assume working,
      // but only briefly. If the polled status hasn't resolved after STALL_MS,
      // treat as stalled rather than spinning forever (covers cases where
      // gravitutor's task-status endpoint hangs without erroring).
      turnState = pendingForMs > STALL_MS ? 'stalled' : 'working'
    }
  } else if (liveStreaming) {
    turnState = 'working'
  } else if (lastMessage?.role === 'user') {
    // Legacy pending turn with no task_id to poll — fall back to a time-based check.
    turnState = pendingForMs > STALL_MS ? 'stalled' : 'working'
  }

  // Constrains chat content to a centered reading column when expanded full-screen.
  const centeredColumn: CSSProperties = {
    maxWidth: expanded ? EXPANDED_COLUMN_MAX_WIDTH : undefined,
    margin: '0 auto',
  }

  return (
    <Drawer
      placement='right'
      mask={false}
      width={expanded ? '100vw' : DEFAULT_DRAWER_WIDTH}
      open={open}
      onClose={onClose}
      title={
        <Horizontal gap={8} verticalCenter>
          <IconSparkles size={18} color={token.colorPrimary} />
          <span>GraviChat</span>
        </Horizontal>
      }
      styles={{ body: { padding: 0 }, footer: { padding: 12 } }}
      extra={
        <Horizontal gap={4} verticalCenter>
          <Tooltip title='New chat'>
            <Button
              type='text'
              icon={<EditOutlined />}
              aria-label='New chat'
              onClick={handleNew}
            />
          </Tooltip>
          <Tooltip title='Chat history'>
            <Button
              type='text'
              icon={<HistoryOutlined />}
              aria-label='Chat history'
              onClick={() => setView('conversations')}
            />
          </Tooltip>
          <Tooltip title={expanded ? 'Exit full screen' : 'Full screen'}>
            <Button
              type='text'
              icon={
                expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />
              }
              aria-label={expanded ? 'Exit full screen' : 'Full screen'}
              onClick={() => setExpanded((v) => !v)}
            />
          </Tooltip>
        </Horizontal>
      }
      footer={
        view === 'chat' ? (
          <div style={centeredColumn}>
            <Composer
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              onSend={handleSend}
              onStop={handleStop}
              // Scope the busy/Stop state to the thread that owns the stream so a
              // background turn in another chat doesn't lock this chat's composer.
              status={streamForActiveThread ? status : 'idle'}
            />
          </div>
        ) : null
      }
    >
      <div
        ref={bodyDragRef}
        style={{ position: 'relative', minHeight: '100%' }}
      >
        {isDragging && view === 'chat' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: token.colorPrimaryBg,
              border: `2px dashed ${token.colorPrimary}`,
              borderRadius: 8,
              pointerEvents: 'none',
            }}
          >
            <Texto weight='bold' style={{ color: token.colorPrimary }}>
              Drop image to attach
            </Texto>
          </div>
        )}
        {view === 'conversations' ? (
          <Vertical style={{ height: '100%' }}>
            <Horizontal
              verticalCenter
              style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                flexShrink: 0,
              }}
            >
              <Button
                type='text'
                icon={<ArrowLeftOutlined />}
                onClick={() => setView('chat')}
              >
                Conversations
              </Button>
            </Horizontal>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ConversationsPanel
                threads={(threads.data ?? []).map((th) => ({
                  id: th.id,
                  title: th.title ?? 'Untitled chat',
                  message_count: th.message_count,
                  updated_at: th.updated_at,
                }))}
                activeId={activeThreadId}
                onSelect={handleSelect}
                onDelete={handleDelete}
              />
            </div>
          </Vertical>
        ) : detail.isLoading ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : (
          <div style={centeredColumn}>
            <MessageList
              messages={messages}
              turnState={turnState}
              turnErrorText={turnErrorText}
              streamActive={streamForActiveThread}
            />
          </div>
        )}
      </div>
    </Drawer>
  )
}
